import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { todayInPH } from '@/lib/phDate';
import { Dialog } from '@/components/ui/Dialog';
import { qk } from '@/lib/queryKeys';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';

interface CycleCloseModalProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  cycleId: string;
  pondName: string;
  cycleNumber?: number | null;
  doc: number;
}

interface CloseFormData {
  status: string;
  actual_harvest_date: string;
  notes: string;
}

const STATUS_OPTIONS = [
  { value: 'harvested', label: 'Harvested' },
  { value: 'failed', label: 'Failed (crop loss)' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function CycleCloseModal({
  open,
  onClose,
  farmId,
  cycleId,
  pondName,
  cycleNumber,
  doc,
}: CycleCloseModalProps) {
  const queryClient = useQueryClient();
  const today = todayInPH();

  const [formData, setFormData] = useState<CloseFormData>({
    status: 'harvested',
    actual_harvest_date: today,
    notes: '',
  });
  const [showConfirm, setShowConfirm] = useState(false);

  const closeMutation = useMutation({
    mutationFn: async (data: CloseFormData) => {
      const payload: Record<string, unknown> = {
        status: data.status,
      };

      if (data.actual_harvest_date) {
        payload.actual_harvest_date = data.actual_harvest_date;
      }
      if (data.notes.trim()) {
        payload.notes = data.notes.trim();
      }

      return api.post(`/api/v1/farms/${farmId}/cycles/${cycleId}/close`, payload);
    },
    onSuccess: () => {
      const statusLabel = STATUS_OPTIONS.find((o) => o.value === formData.status)?.label || formData.status;
      toast.success(`Cycle closed: ${statusLabel}`);
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      queryClient.invalidateQueries({ queryKey: ['cycles', farmId] });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to close cycle');
    },
  });

  const handleClose = () => {
    setFormData({
      status: 'harvested',
      actual_harvest_date: today,
      notes: '',
    });
    setShowConfirm(false);
    onClose();
  };

  const handleChange = (field: keyof CloseFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!showConfirm) {
      setShowConfirm(true);
    } else {
      closeMutation.mutate(formData);
    }
  };

  const cycleLabel = cycleNumber ? `Cycle #${cycleNumber}` : 'Current Cycle';

  return (
    <Dialog open={open} onClose={handleClose} title={`Close Cycle - ${pondName}`} size="md">
      {!showConfirm ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  You are closing {cycleLabel} at DOC {doc}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  This action cannot be undone. Historical data will be preserved.
                </p>
              </div>
            </div>
          </div>

          <Select
            label="Closing Reason"
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            options={STATUS_OPTIONS}
          />

          <Input
            label="Actual Harvest/Close Date"
            type="date"
            value={formData.actual_harvest_date}
            onChange={(e) => handleChange('actual_harvest_date', e.target.value)}
          />

          <Textarea
            label="Notes"
            placeholder="Final notes about this cycle (harvest weight, observations, etc.)..."
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={closeMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="danger">
              Close Cycle
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Are you sure you want to close this cycle?
                </p>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  <li>
                    Status:{' '}
                    <span className="font-medium">
                      {STATUS_OPTIONS.find((o) => o.value === formData.status)?.label}
                    </span>
                  </li>
                  <li>
                    Close Date:{' '}
                    <span className="font-medium">{formData.actual_harvest_date}</span>
                  </li>
                  {formData.notes && (
                    <li>
                      Notes: <span className="font-medium">{formData.notes}</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowConfirm(false)}
              disabled={closeMutation.isPending}
            >
              Go Back
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={closeMutation.isPending}
              onClick={() => closeMutation.mutate(formData)}
            >
              Confirm Close
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
